import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useCurrentUser() {
  return useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      const res = await fetch("/auth/me");

      if (res.status === 401) {
        return null;
      }

      if (!res.ok) {
        throw new Error("Failed to fetch user");
      }

      return res.json();
    },
    retry: false,
  });
}

export function useHistoryMetrics(months: number = 6) {
  return useQuery({
    queryKey: ["historyMetrics", months],
    queryFn: async () => {
      const res = await fetch(`/api/metrics/history?months=${months}`);
      if (!res.ok) {
        throw new Error("Failed to fetch history metrics");
      }
      return res.json();
    },
    retry: false,
  });
}

export function useWeeklyHistoryMetrics(weeks: number = 4) {
  return useQuery({
    queryKey: ["weeklyHistoryMetrics", weeks],
    queryFn: async () => {
      const res = await fetch(`/api/metrics/weekly_history?weeks=${weeks}`);
      if (!res.ok) {
        throw new Error("Failed to fetch weekly history metrics");
      }
      return res.json();
    },
    retry: false,
  });
}

// --- Marketing Updates ---

export interface MarketingActivity {
  id: number;
  segment: string;
  title: string;
  description: string | null;
  activity_type: string | null;
  status: string;
  activity_date: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActivityCreate {
  segment: string;
  title: string;
  description?: string;
  activity_type?: string;
  status?: string;
  activity_date?: string;
}

export interface ActivityUpdate {
  title?: string;
  description?: string;
  activity_type?: string;
  status?: string;
  activity_date?: string;
}

export function useMarketingActivities(segment?: string) {
  return useQuery<MarketingActivity[]>({
    queryKey: ["marketingActivities", segment],
    queryFn: async () => {
      const url = segment
        ? `/api/marketing-updates?segment=${segment}`
        : "/api/marketing-updates";
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch marketing activities");
      return res.json();
    },
  });
}

export function useCreateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ActivityCreate) => {
      const res = await fetch("/api/marketing-updates", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to create activity");
      return res.json() as Promise<MarketingActivity>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketingActivities"] }),
  });
}

export function useUpdateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...body }: ActivityUpdate & { id: number }) => {
      const res = await fetch(`/api/marketing-updates/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to update activity");
      return res.json() as Promise<MarketingActivity>;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketingActivities"] }),
  });
}

// --- Instantly.ai email campaigns ---

export interface InstantlyCampaign {
  id: string;
  name: string;
  segment: string;       // "smb" | "mid_market" | "channels"
  status: string;        // "active" | "paused" | "completed" | "draft"
  status_code: number;
  leads_count: number;
  contacted: number;
  emails_sent: number;
  open_count: number;
  reply_count: number;
  link_click_count: number;
  bounced_count: number;
  unsubscribed_count: number;
  completed: number;
  opportunities: number;
}

export function useInstantlyCampaigns() {
  return useQuery<InstantlyCampaign[]>({
    queryKey: ["instantlyCampaigns"],
    queryFn: async () => {
      const res = await fetch("/api/instantly/campaigns");
      if (res.status === 400) {
        // Not configured — return empty so the UI can show a setup prompt
        return [];
      }
      if (!res.ok) throw new Error("Failed to fetch Instantly campaigns");
      return res.json();
    },
    staleTime: 5 * 60 * 1000, // 5 min — Instantly data doesn't change by the second
  });
}

export function useDeleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/marketing-updates/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete activity");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["marketingActivities"] }),
  });
}
